import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { DollarSign, Plus, Users, ThumbsUp } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { formatCurrency, Currency } from "@/lib/currency";

interface BudgetSuggestion {
  id: string;
  suggester_email: string;
  suggester_name?: string | null;
  budget_min: number;
  budget_max: number;
  votes: string[];
  vote_count: number;
  created_at: string;
}

interface DiscussionBudgetProps {
  entityType: "event" | "purchase";
  entityId: string;
  userEmail: string;
  participantCount: number;
  currency: string;
  targetBudgetMin?: number | null;
  targetBudgetMax?: number | null;
  themeColor: string;
}

export function DiscussionBudget({
  entityType,
  entityId,
  userEmail,
  participantCount,
  currency,
  targetBudgetMin,
  targetBudgetMax,
  themeColor,
}: DiscussionBudgetProps) {
  const [suggestions, setSuggestions] = useState<BudgetSuggestion[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [customMin, setCustomMin] = useState("");
  const [customMax, setCustomMax] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const tableName = entityType === "event" ? "event_discussions" : "group_purchase_discussions";
  const foreignKey = entityType === "event" ? "reminder_id" : "purchase_id";

  useEffect(() => {
    fetchSuggestions();
  }, [entityId]);

  const fetchSuggestions = async () => {
    let data: any[] = [];
    let error: any = null;

    if (entityType === "event") {
      const result = await supabase
        .from("event_discussions")
        .select("*")
        .eq("reminder_id", entityId)
        .eq("message_type", "budget_suggestion")
        .order("vote_count", { ascending: false });
      data = result.data || [];
      error = result.error;
    } else {
      const result = await supabase
        .from("group_purchase_discussions")
        .select("*")
        .eq("purchase_id", entityId)
        .eq("message_type", "budget_suggestion")
        .order("vote_count", { ascending: false });
      data = result.data || [];
      error = result.error;
    }

    if (!error && data) {
      const mapped: BudgetSuggestion[] = data.map((d: any) => {
        const match = d.message.match(/(\d+)\s*-\s*(\d+)/);
        return {
          id: d.id,
          suggester_email: d.sender_email,
          suggester_name: d.sender_name,
          budget_min: match ? parseInt(match[1]) : 0,
          budget_max: match ? parseInt(match[2]) : 0,
          votes: Array.isArray(d.votes) ? d.votes : [],
          vote_count: d.vote_count || 0,
          created_at: d.created_at,
        };
      });
      setSuggestions(mapped);
    }
  };

  const handleAddSuggestion = async () => {
    const min = parseFloat(customMin);
    const max = parseFloat(customMax);

    if (isNaN(min) || isNaN(max) || min <= 0 || max <= 0) {
      toast.error("Please enter valid amounts");
      return;
    }

    if (min > max) {
      toast.error("Minimum should be less than maximum");
      return;
    }

    setSubmitting(true);
    try {
      let error: any = null;

      if (entityType === "event") {
        const result = await supabase
          .from("event_discussions")
          .insert({
            reminder_id: entityId,
            sender_email: userEmail,
            sender_name: null,
            message: `₹${min} - ₹${max}`,
            message_type: "budget_suggestion",
            votes: [userEmail],
            vote_count: 1,
          });
        error = result.error;
      } else {
        const result = await supabase
          .from("group_purchase_discussions")
          .insert({
            purchase_id: entityId,
            sender_email: userEmail,
            sender_name: null,
            message: `₹${min} - ₹${max}`,
            message_type: "budget_suggestion",
            votes: [userEmail],
            vote_count: 1,
          });
        error = result.error;
      }

      if (error) throw error;

      toast.success("Budget suggestion added!");
      setShowAddForm(false);
      setCustomMin("");
      setCustomMax("");
      fetchSuggestions();
    } catch (error) {
      console.error("Error adding suggestion:", error);
      toast.error("Failed to add suggestion");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVote = async (suggestionId: string) => {
    const suggestion = suggestions.find((s) => s.id === suggestionId);
    if (!suggestion) return;

    const hasVoted = suggestion.votes.includes(userEmail);
    const newVotes = hasVoted
      ? suggestion.votes.filter((v) => v !== userEmail)
      : [...suggestion.votes, userEmail];

    try {
      const { error } = await supabase
        .from(tableName)
        .update({
          votes: newVotes,
          vote_count: newVotes.length,
        })
        .eq("id", suggestionId);

      if (error) throw error;
      fetchSuggestions();
    } catch (error) {
      console.error("Error voting:", error);
      toast.error("Failed to vote");
    }
  };

  // Calculate consensus from votes
  const topSuggestion = suggestions.length > 0 ? suggestions[0] : null;
  const avgMid = topSuggestion
    ? (topSuggestion.budget_min + topSuggestion.budget_max) / 2
    : 0;
  const perPerson = participantCount > 0 && avgMid > 0 ? avgMid / participantCount : 0;

  const getThemeClasses = () => {
    switch (themeColor) {
      case "pink":
        return {
          button: "bg-pink-600 hover:bg-pink-700 text-white",
          accent: "text-pink-600",
          border: "border-pink-200 dark:border-pink-900/30",
          bg: "bg-pink-500/5",
          votedBg: "bg-pink-100 dark:bg-pink-900/30",
        };
      case "emerald":
        return {
          button: "bg-emerald-600 hover:bg-emerald-700 text-white",
          accent: "text-emerald-600",
          border: "border-emerald-200 dark:border-emerald-900/30",
          bg: "bg-emerald-500/5",
          votedBg: "bg-emerald-100 dark:bg-emerald-900/30",
        };
      default:
        return {
          button: "bg-purple-600 hover:bg-purple-700 text-white",
          accent: "text-purple-600",
          border: "border-purple-200 dark:border-purple-900/30",
          bg: "bg-purple-500/5",
          votedBg: "bg-purple-100 dark:bg-purple-900/30",
        };
    }
  };

  const theme = getThemeClasses();

  return (
    <div className="space-y-4">
      {/* Top Consensus */}
      {topSuggestion && (
        <Card className={theme.border}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium flex items-center gap-2">
                <DollarSign className={`w-4 h-4 ${theme.accent}`} />
                Top Budget
              </h3>
              <Badge variant="outline" className="gap-1">
                <Users className="w-3 h-3" />
                {topSuggestion.vote_count} votes
              </Badge>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Range:</span>
                <span className="font-medium">
                  {formatCurrency(topSuggestion.budget_min, currency as Currency)} -{" "}
                  {formatCurrency(topSuggestion.budget_max, currency as Currency)}
                </span>
              </div>

              {participantCount > 0 && perPerson > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    Per person (~{participantCount}):
                  </span>
                  <span className={`font-medium ${theme.accent}`}>
                    ~{formatCurrency(perPerson, currency as Currency)}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Budget Button */}
      <div className="flex justify-between items-center">
        <h4 className="text-sm font-medium text-muted-foreground">
          Budget Suggestions ({suggestions.length})
        </h4>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowAddForm(!showAddForm)}
          className="gap-2"
        >
          <Plus className="w-4 h-4" />
          Suggest Budget
        </Button>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <Card className={theme.border}>
          <CardContent className="p-4 space-y-3">
            <h4 className="text-sm font-medium">Enter your budget range:</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Minimum (₹)</label>
                <Input
                  type="number"
                  placeholder="e.g., 1500"
                  value={customMin}
                  onChange={(e) => setCustomMin(e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Maximum (₹)</label>
                <Input
                  type="number"
                  placeholder="e.g., 3000"
                  value={customMax}
                  onChange={(e) => setCustomMax(e.target.value)}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleAddSuggestion}
                disabled={submitting}
                className={theme.button}
              >
                {submitting ? "Adding..." : "Add Suggestion"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowAddForm(false)}
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Suggestions - Votable */}
      <ScrollArea className="h-[250px]">
        {suggestions.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <DollarSign className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No budget suggestions yet</p>
            <p className="text-xs mt-1">Be the first to suggest a budget!</p>
          </div>
        ) : (
          <div className="space-y-2">
            {suggestions.map((suggestion) => {
              const hasVoted = suggestion.votes.includes(userEmail);
              const isOwn = suggestion.suggester_email === userEmail;

              return (
                <div
                  key={suggestion.id}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    hasVoted ? theme.votedBg : "bg-card"
                  } ${theme.border}`}
                >
                  <div className="flex-1">
                    <p className="font-medium text-sm">
                      {formatCurrency(suggestion.budget_min, currency as Currency)} -{" "}
                      {formatCurrency(suggestion.budget_max, currency as Currency)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      by{" "}
                      {isOwn
                        ? "You"
                        : suggestion.suggester_name ||
                          suggestion.suggester_email.split("@")[0]}
                      {participantCount > 0 && (
                        <span className="ml-2">
                          (~
                          {formatCurrency(
                            (suggestion.budget_min + suggestion.budget_max) /
                              2 /
                              participantCount,
                            currency as Currency
                          )}
                          /person)
                        </span>
                      )}
                    </p>
                  </div>
                  <Button
                    variant={hasVoted ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleVote(suggestion.id)}
                    className={`gap-1 ${hasVoted ? theme.button : ""}`}
                  >
                    <ThumbsUp
                      className={`w-3 h-3 ${hasVoted ? "fill-current" : ""}`}
                    />
                    {suggestion.vote_count}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
